import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { motion, AnimatePresence } from 'motion/react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, Leaf, AlertCircle } from 'lucide-react';
import { LeafCluster } from '../components/WoodlandSVG';
import {
  fadeUp, fadeLeft, fadeRight, scaleIn,
  staggerContainer, staggerItem, pageTransition, viewportConfig
} from '../components/AnimationVariants';

const hours = [
  { day: 'Monday', time: '9:00 AM – 6:00 PM', open: true },
  { day: 'Tuesday', time: '9:00 AM – 6:00 PM', open: true },
  { day: 'Wednesday', time: '9:00 AM – 6:00 PM', open: true },
  { day: 'Thursday', time: '9:00 AM – 6:00 PM', open: true },
  { day: 'Friday', time: '9:00 AM – 6:00 PM', open: true },
  { day: 'Saturday', time: 'Closed', open: false },
  { day: 'Sunday', time: 'Closed', open: false },
];

const TREATMENT_TYPES = [
  'General Dentistry',
  'Restorative Dentistry',
  'Root Canal Treatments',
  'Invisalign®',
  'Teeth Whitening',
  'Porcelain Veneers',
  'Composite Bonding',
  'Dental Implants',
  'Facial Aesthetics',
  'Dental Emergencies',
  'TMJ Disorder',
];

const PREFERRED_TIMES = [
  'Morning (9:00 AM – 12:00 PM)',
  'Afternoon (12:00 PM – 3:00 PM)',
  'Late Afternoon (3:00 PM – 6:00 PM)',
  'Saturday Morning (9:00 AM – 12:00 PM)',
];

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Name is required.';
  if (!form.email.trim()) errors.email = 'Email is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Please enter a valid email.';
  if (form.phone && !/^[\d\s\-()+]{7,}$/.test(form.phone)) errors.phone = 'Please enter a valid phone number.';
  return errors;
}

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', date: '', preferredTime: '', treatmentType: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [focused, setFocused] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((err) => ({ ...err, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      await emailjs.send(
        'service_wmnofak',
        'template_sjjhvvr',
        { name: form.name, reply_to: form.email, phone: form.phone, date: form.date, preferred_time: form.preferredTime, treatment_type: form.treatmentType, message: form.message },
        'RiI1vF3ulo3Ib-l9m'
      );
      setSubmitted(true);
    } catch (err) {
      console.error('EmailJS error:', err);
      setErrors({ form: `Error: ${err?.text || err?.message || JSON.stringify(err)}` });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full rounded-xl border px-4 py-3 text-sm outline-none transition-all duration-200 bg-white ${
      errors[field] ? 'border-red-400' : focused === field ? 'border-forest ring-2 ring-forest/15' : 'border-cream-deeper'
    }`;

  const selectClass = (field) =>
    `w-full rounded-xl border px-4 py-3 text-sm outline-none transition-all duration-200 bg-white appearance-none cursor-pointer ${
      errors[field] ? 'border-red-400' : focused === field ? 'border-forest ring-2 ring-forest/15' : 'border-cream-deeper'
    }`;

  return (
    <motion.div {...pageTransition} className="overflow-x-hidden pt-24">

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-forest-dark to-forest overflow-hidden">
        <motion.div className="absolute right-0 top-0 w-64 opacity-15 hidden md:block"
          animate={{ y: [0, -10, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}>
          <LeafCluster />
        </motion.div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p className="text-sage-light text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <Leaf className="w-4 h-4" /> Get in Touch
          </motion.p>
          <motion.h1 className="text-4xl md:text-5xl font-serif text-white font-semibold mb-5"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            We'd Love to Hear From You
          </motion.h1>
          <motion.p className="text-cream/75 text-lg leading-relaxed"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
            Book an appointment, ask a question, or just say hello.
          </motion.p>
        </div>
      </section>

      {/* Main content */}
      <section className="section-padding bg-cream">
        <div className="container-max">
          <div className="grid lg:grid-cols-5 gap-10">

            {/* Form */}
            <motion.div className="lg:col-span-3" variants={fadeLeft} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-cream-deeper">
                <h2 className="font-serif text-forest text-2xl font-semibold mb-2">Book an Appointment</h2>
                <p className="text-gray-500 text-sm mb-6">We'll confirm your appointment within one business day.</p>

                <AnimatePresence mode="wait">
                  {submitted ? (
                    <motion.div key="success" className="text-center py-12"
                      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 300 } }}
                      exit={{ opacity: 0 }}>
                      <motion.div className="w-20 h-20 rounded-full bg-sage-pale flex items-center justify-center mx-auto mb-5"
                        initial={{ scale: 0 }} animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 15, delay: 0.1 }}>
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}>
                          <CheckCircle className="w-10 h-10 text-forest" />
                        </motion.div>
                      </motion.div>
                      <motion.h3 className="font-serif text-forest text-2xl font-semibold mb-2"
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                        Request Received!
                      </motion.h3>
                      <motion.p className="text-gray-500 leading-relaxed max-w-sm mx-auto"
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                        Thank you, <strong className="text-forest">{form.name}</strong>! We'll reach out to{' '}
                        <strong className="text-forest">{form.email}</strong> within one business day.
                      </motion.p>
                      <motion.button className="mt-6 btn-outline text-sm"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
                        onClick={() => { setForm({ name: '', email: '', phone: '', date: '', preferredTime: '', treatmentType: '', message: '' }); setSubmitted(false); }}>
                        Submit Another Request
                      </motion.button>
                    </motion.div>
                  ) : (
                    <motion.form key="form" onSubmit={handleSubmit} noValidate
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <div className="grid sm:grid-cols-2 gap-5 mb-5">
                        {[
                          { id: 'name', label: 'Full Name', type: 'text', placeholder: 'Jane Doe', required: true, autoComplete: 'name' },
                          { id: 'email', label: 'Email Address', type: 'email', placeholder: 'jane@email.com', required: true, autoComplete: 'email' },
                          { id: 'phone', label: 'Phone Number', type: 'tel', placeholder: '(555) 000-0000', required: false, autoComplete: 'tel' },
                          { id: 'date', label: 'Preferred Date', type: 'date', required: false },
                        ].map((field) => (
                          <motion.div key={field.id} whileHover={{ y: -1 }} transition={{ type: 'spring', stiffness: 400 }}>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor={field.id}>
                              {field.label} {field.required && <span className="text-red-400">*</span>}
                            </label>
                            <input id={field.id} name={field.id} type={field.type} value={form[field.id]}
                              onChange={handleChange} placeholder={field.placeholder}
                              onFocus={() => setFocused(field.id)} onBlur={() => setFocused(null)}
                              min={field.type === 'date' ? new Date().toISOString().split('T')[0] : undefined}
                              aria-describedby={field.type === 'date' ? 'date-hint' : undefined}
                              className={inputClass(field.id)} autoComplete={field.autoComplete} />
                            {field.type === 'date' && (
                              <span id="date-hint" className="sr-only">Earliest available date is today</span>
                            )}
                            <AnimatePresence>
                              {errors[field.id] && (
                                <motion.p className="mt-1 text-xs text-red-500 flex items-center gap-1"
                                  role="alert"
                                  initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                                  <AlertCircle className="w-3 h-3" />{errors[field.id]}
                                </motion.p>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        ))}

                        {/* Preferred Time */}
                        <motion.div whileHover={{ y: -1 }} transition={{ type: 'spring', stiffness: 400 }}>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="preferredTime">
                            Preferred Time
                          </label>
                          <div className="relative">
                            <select id="preferredTime" name="preferredTime" value={form.preferredTime}
                              onChange={handleChange} onFocus={() => setFocused('preferredTime')} onBlur={() => setFocused(null)}
                              className={selectClass('preferredTime')}>
                              <option value="">Select a time slot…</option>
                              {PREFERRED_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </motion.div>

                        {/* Treatment Type */}
                        <motion.div whileHover={{ y: -1 }} transition={{ type: 'spring', stiffness: 400 }}>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="treatmentType">
                            Treatment Type
                          </label>
                          <div className="relative">
                            <select id="treatmentType" name="treatmentType" value={form.treatmentType}
                              onChange={handleChange} onFocus={() => setFocused('treatmentType')} onBlur={() => setFocused(null)}
                              className={selectClass('treatmentType')}>
                              <option value="">Select a treatment…</option>
                              {TREATMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </motion.div>
                      </div>

                      <motion.div className="mb-6" whileHover={{ y: -1 }} transition={{ type: 'spring', stiffness: 400 }}>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="message">
                          How Can We Help? <span className="text-gray-400 font-normal text-xs">(optional)</span>
                        </label>
                        <textarea id="message" name="message" rows={4} value={form.message}
                          onChange={handleChange} onFocus={() => setFocused('message')} onBlur={() => setFocused(null)}
                          placeholder="Tell us about any concerns, or whether you're a new or returning patient…"
                          className={`${inputClass('message')} resize-none`} />
                      </motion.div>
                      {errors.form && (
                        <p className="mb-3 text-sm text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />{errors.form}
                        </p>
                      )}
                      <motion.button type="submit" disabled={submitting}
                        className="btn-primary w-full justify-center text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
                        whileHover={{ scale: 1.02, boxShadow: '0 8px 24px rgba(197,165,90,0.3)' }}
                        whileTap={{ scale: 0.98 }}>
                        <Send className="w-5 h-5" /> {submitting ? 'Sending…' : 'Send Appointment Request'}
                      </motion.button>
                      <p className="text-xs text-gray-400 text-center mt-3">For emergencies, please call us directly.</p>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div className="lg:col-span-2 space-y-6" variants={fadeRight} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              {/* Contact info */}
              <motion.div className="bg-white rounded-2xl p-6 shadow-sm border border-cream-deeper"
                variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
                <h3 className="font-serif text-forest text-lg font-semibold mb-5">Contact Information</h3>
                {[
                  { icon: MapPin, label: 'Our Location', content: '106 Woodlands\nNorth Harrow, HA2 6EW', href: null },
                  { icon: Phone, label: 'Phone', content: '020 8866 5556', href: 'tel:+442088665556' },
                  { icon: Mail, label: 'Email', content: 'hello@wc4d.co.uk', href: 'mailto:hello@wc4d.co.uk' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div key={i} variants={staggerItem} className="flex items-start gap-3 mb-4 last:mb-0">
                      <motion.div className="w-9 h-9 rounded-lg bg-sage-pale flex items-center justify-center flex-shrink-0 mt-0.5"
                        whileHover={{ scale: 1.1, backgroundColor: '#1B3A2D' }}
                        transition={{ type: 'spring', stiffness: 400 }}>
                        <Icon className="w-4 h-4 text-forest" />
                      </motion.div>
                      <div>
                        <div className="font-medium text-gray-700 text-sm">{item.label}</div>
                        {item.href
                          ? <a href={item.href} className="text-forest hover:text-forest-light text-sm transition-colors">{item.content}</a>
                          : <div className="text-gray-500 text-sm whitespace-pre-line">{item.content}</div>
                        }
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Hours */}
              <motion.div className="bg-white rounded-2xl p-6 shadow-sm border border-cream-deeper"
                variants={scaleIn} initial="hidden" whileInView="visible" viewport={viewportConfig}>
                <h3 className="font-serif text-forest text-lg font-semibold mb-5 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-sage" /> Office Hours
                </h3>
                <div className="space-y-2">
                  {hours.map((h, i) => (
                    <motion.div key={h.day}
                      className="flex justify-between items-center text-sm py-1.5 border-b border-cream-deeper last:border-0"
                      initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }} viewport={{ once: true }}>
                      <span className={`font-medium ${!h.open ? 'text-gray-400' : 'text-gray-700'}`}>{h.day}</span>
                      <span className={`${!h.open ? 'text-gray-400 italic' : 'text-forest font-medium'}`}>{h.time}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Emergency */}
              <motion.div className="bg-forest rounded-2xl p-5 text-white"
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}
                whileHover={{ scale: 1.02 }} transition={{ type: 'spring', stiffness: 300 }}>
                <h4 className="font-serif font-semibold mb-2">Dental Emergency?</h4>
                <p className="text-cream/75 text-sm mb-3">Don't wait. Same-day emergency slots always available.</p>
                <motion.a href="tel:+442088665556" className="btn-primary text-sm py-2.5 justify-center"
                  whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                  <Phone className="w-4 h-4" /> Call Now
                </motion.a>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="px-4 pb-16 bg-cream">
        <div className="container-max">
          <motion.div className="rounded-3xl overflow-hidden shadow-sm border border-cream-deeper bg-white"
            variants={scaleIn} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <div className="relative h-64 md:h-80 overflow-hidden">
              <iframe
                title="Woodlands Orthodontics location"
                src="https://maps.google.com/maps?q=106+Woodlands,+North+Harrow,+HA2+6EW&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="px-6 py-4 bg-white border-t border-cream-deeper flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-sm text-gray-500">Off-road parking on-site · Near North Harrow &amp; Pinner tube stations · 183 bus route</p>
              <motion.a href="https://maps.google.com/maps?q=106+Woodlands,+North+Harrow,+HA2+6EW" target="_blank" rel="noopener noreferrer"
                className="btn-outline text-sm py-2"
                whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                Get Directions <MapPin className="w-4 h-4" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}
